import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CompassTool, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { http } from "../lib/api";

export default function Login() {
  const nav = useNavigate();
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    http.get("/marketing/login-slider")
      .then(({ data }) => setPhotos((data?.photos || []).slice(0, 5)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (photos.length < 2) return;
    const t = setInterval(() => setIdx((n) => (n + 1) % photos.length), 5000);
    return () => clearInterval(t);
  }, [photos.length]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email.trim(), password);
    setLoading(false);
    if (ok) {
      toast.success("Welcome back");
      nav("/board");
    } else {
      toast.error("Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: photo slider (5 sec auto-advance) */}
      <div className="hidden lg:block relative border-r border-zinc-300 bg-zinc-900 overflow-hidden" data-testid="login-slider">
        {photos.length === 0 ? (
          <div className="absolute inset-0 bg-zinc-900" />
        ) : (
          photos.map((src, i) => (
            <img
              key={`${src}-${i}`}
              src={src}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ${i === idx ? "opacity-100" : "opacity-0"}`}
              data-testid={`login-slide-${i}`}
            />
          ))
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/85 via-zinc-900/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border-2 border-orange-500 flex items-center justify-center">
              <CompassTool size={22} weight="bold" className="text-orange-500" />
            </div>
            <span className="label-overline text-orange-500">Dream Home Planners</span>
          </div>
          <div className="mt-auto">
            <div className="label-overline text-blue-300 mb-4">Studio Operations · v1</div>
            <h1 className="font-display text-5xl font-black tracking-tighter uppercase leading-none">
              Draft. Assign.<br />Deliver.
            </h1>
            <p className="mt-6 max-w-md text-zinc-200 leading-relaxed">
              The internal task board built specifically for our studio — from concept
              sketches to construction handover.
            </p>
            {photos.length > 1 && (
              <div className="mt-8 flex items-center gap-1.5" data-testid="login-slider-dots">
                {photos.map((_, i) => (
                  <button
                    key={`login-dot-${i}`}
                    onClick={() => setIdx(i)}
                    aria-label={`Photo ${i + 1}`}
                    className={`h-1 transition-all ${i === idx ? "w-8 bg-orange-500" : "w-2 bg-white/50 hover:bg-white/80"}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 bg-zinc-50">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="label-overline text-blue-600 mb-3">Access · Restricted</div>
            <h2 className="font-display text-4xl font-black tracking-tighter uppercase text-zinc-900">
              Sign in to the studio
            </h2>
            <p className="mt-3 text-zinc-600">Only assigned team members may proceed.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-overline block mb-2 text-zinc-700">Email or Contact No.</label>
              <Input
                data-testid="login-email-input"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none h-12 border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <label className="label-overline block mb-2 text-zinc-700">Password</label>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none h-12 border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="border-l-4 border-red-600 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-none h-12 bg-zinc-900 hover:bg-blue-600 text-white font-mono uppercase tracking-widest text-sm group"
            >
              {loading ? "Authenticating…" : "Enter Studio"}
              <ArrowRight size={16} weight="bold" className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <div className="text-center pt-2">
              <a href="/forgot-password" data-testid="forgot-password-link"
                 className="label-overline text-zinc-500 hover:text-blue-600">
                Forgot Password?
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

